import { Component, OnInit } from '@angular/core';
import { Debtor } from 'src/app/models/debtor.model';
import { DebtorService } from 'src/app/services/debtor.service';
import * as _ from 'lodash';
import { Common } from 'src/app/common';
import { AccountSummary, DebtorAccount } from 'src/app/models/debtor-summary.model';
import { SessionService } from 'src/app/services/session.service';
import { ConfigurationService } from 'src/app/services/configuration.service';
import { AppConfigService } from 'src/app/services/app-config.service';

@Component({
  selector: 'app-debt-summary',
  templateUrl: './debt-summary.component.html',
  styleUrls: ['./debt-summary.component.scss']
})
export class DebtSummaryComponent implements OnInit {
  //
  // There are 3 'scenarios' for a debt summary, which depends on the type of debt accounts as follows:
  // OS - All debts are outstanding
  // PP - All debts are on payment plan
  // MIX - Mixed bag on payment plan and not
  //
  // For further information on this refer to PBI #141
  //
  public debtScenario: string = '';
  public accountSummary = new AccountSummary();
  public debtor = new Debtor;
  public initialised = false;

  constructor(
    private debtorService: DebtorService,
    private appConfigService: AppConfigService,
    private configurationService: ConfigurationService,
    private sessionService: SessionService) { }

  ngOnInit(): void {
    setTimeout(() => this.loadAccountSummary(), 0);
  }

  private loadAccountSummary(): void {
    this.sessionService.showLoading();

    this.debtorService.getAccountSummary().subscribe({
      next: (result) => this.loadAccountSummaryOk(result),
      error: (error) => this.loadAccountSummaryError(error.error)
    });
  }

  private loadAccountSummaryOk(accountSummary: AccountSummary) {
    this.accountSummary = accountSummary;
    this.setDebtScenario();
    this.initialised = true;

    this.sessionService.hideLoading();
  }

  private loadAccountSummaryError(error: any) {
    this.sessionService.hideLoading();

    this.sessionService.showErrorDialog('Error loading debt details. Please refresh the page to try again.');
  }

  public getTotalDebtFormatted(): string {
    return `${this.formatCurrency(this.getTotalDebt())}`;
  }

  public getTotalOutstandingFormatted(): string {
    return `${this.formatCurrency(this.getTotalOutstanding())}`;
  }

  public getTotalOutstandingWithCurrency(): string {
    return `${this.getTotalOutstandingFormatted()} ${this.accountSummary.dtrAccSumm.currency}`;
  }

  public getTotalPaidFormatted(): string {
    return `${this.formatCurrency(this.getTotalPaid())}`;
  }

  public getTotalDebt(): number {
    return this.getTotalPaid() + this.getTotalOutstanding();
  }

  public getTotalPaid(): number {
    let result = 0;

    this.accountSummary.dtrAccSumm.accounts.forEach((account) => {
      result += account.totalPaid;
    });

    return result;
  }

  public getTotalOutstanding(): number {
    let result = 0;

    this.accountSummary.dtrAccSumm.accounts.forEach((account) => {
      result += account.totalOutstanding;
    });

    return result;
  }

  public getTotalOutstandingNotOnPlanFormatted(): string {
    let result = 0;

    this.accountSummary.dtrAccSumm.accounts.forEach((account) => {
      if (account.statusCode !== 'PP') {
        result += account.totalOutstanding;
      }
    });

    return this.formatCurrency(result);
  }

  public formatCurrency(amount: number): string {
    return Common.getFormattedCurrencyValue(amount, false, false);
  }

  public isPaymentPlanButtonVisible(): boolean {
    return this.debtScenario === 'OS' && this.isPaymentButtonsVisible();
  }

  public isPaymentPlanLinkVisible(): boolean {
    return this.debtScenario !== 'OS' && this.isPaymentButtonsVisible();
  }

  public isAddPaymentPlanButtonVisible(account: DebtorAccount): boolean {
    return this.debtScenario !== 'OS' && account.statusCode === 'OS' && this.isPaymentButtonsVisible();
  }

  public isPaymentButtonsVisible(): boolean {
    return this.getTotalOutstanding() !== 0;
  }

  public getDebtorAccounts(): Array<DebtorAccount> {
    return _.orderBy(this.accountSummary.dtrAccSumm.accounts, ['priority', 'totalOutstanding'], ['asc', 'desc']);
  }

  public getCircleGaugeColour(): string {
    return this.configurationService.getCircleGaugeColour();
  }

  public isCollexusGraphs(): boolean {
    return this.appConfigService.getConfig().htmlVersion !== 'version1';
  }

  public getScaleLabelContent(value: any): any {
    // If the scale label is 0, it means it's the first element
    // We will instead use the paid value for this
    if (value?.value === 0) {
      value.text = this;
    }

    return value?.text;
  }

  private setDebtScenario(): void {
    const outstandingCount = _.filter(this.accountSummary.dtrAccSumm.accounts, (account) => account.statusCode === 'OS').length;
    const paymentPlanCount = _.filter(this.accountSummary.dtrAccSumm.accounts, (account) => account.statusCode === 'PP').length;

    if (outstandingCount === 0 && paymentPlanCount === 0) {
      this.debtScenario = '';
    } else if (outstandingCount > 0 && paymentPlanCount === 0) {
      this.debtScenario = 'OS';
    } else if (outstandingCount === 0 && paymentPlanCount > 0) {
      this.debtScenario = 'PP';
    } else {
      this.debtScenario = 'MIX';
    }
  }
}
